import React, { useRef, useEffect, useState } from 'react';
import Lottie from 'lottie-react';

// Components
import ProgressBar from './progress-bar';
import LineItem from './line-item';
import Recommendations from './recommendations';
import EmptyCart from './empty-cart';

// Utility functions
import { getDisplayPrice } from '~js/utils/utils';
import {
  updateCartCount,
  getProgressBarDetails,
  getRecommendedProducts,
  handleFreeGift,
} from '~js/utils/cart-drawer-utils';
import { renderIcon } from '~js/utils/react-utils';
import { disableBodyScroll, enableBodyScroll } from '~js/utils/dom';

// Animations
import confettiSpray from '~/animations/confetti-spray.json';

const schemaConfigScript = document.getElementById('cart-drawer-config');
const cartConfigData = schemaConfigScript
  ? JSON.parse(schemaConfigScript.textContent)
  : {};
const cartConfig = {
  ...cartConfigData.section,
  ...cartConfigData.others,
};
window.cartConfig = cartConfig;

const CartDrawer = () => {
  const [cartDrawerLoaded, setCartDrawerLoaded] = useState(false);
  const [cartState, setCartState] = useState({
    itemCount: 0,
    subtotal: 0,
    subtotalVal: 0,
    lineItems: [],
  });
  const [loading, setLoading] = useState(null);
  const [progressBarDetails, setProgressBarDetails] = useState({
    promotionText: '',
    progressPercentage: 0,
    freeShippingPosition: 0,
    hasFreeShipping: false,
    hasFreeGift: false,
  });
  const [recommendedProducts, setRecommendedProducts] = useState(null);
  const [showconfettiSpray, setShowconfettiSpray] = useState(false);
  const [subsTooltip, setSubsTooltip] = useState({ visibleKey: null });
  const dialogRef = useRef(null);

  // Initial render one time
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.addEventListener('close', runOnDialogClose);

    const initializeCartDrawer = async () => {
      await fetchCart(); // Fetch cart on render

      // Define global CartDrawer functions
      window.CartDrawer = {
        show,
        hide,
        fetch: fetchCart,
      };

      // Show Cart Drawer in case of redirect
      if (window.location.search.includes('viewcart=true')) {
        show(false);
      }
    };

    initializeCartDrawer();

    // Cleanup event listener and global functions on unmount
    return () => {
      dialog?.removeEventListener('close', runOnDialogClose);
      delete window.CartDrawer;
    };
  }, []);

  // When cartState.subtotalVal changes
  useEffect(() => {
    const progressBarData = getProgressBarDetails(cartState, cartConfig);
    setProgressBarDetails(progressBarData);

    handleFreeGift(cartState, cartConfig, setShowconfettiSpray);
  }, [cartState.subtotalVal]);

  // When cartState.lineItems changes
  useEffect(() => {
    if (!cartConfig.show_recommendations) {
      return;
    }

    const fetchRecommendations = async () => {
      const recommendations = await getRecommendedProducts(
        cartState.lineItems,
        cartConfig,
      );
      setRecommendedProducts(recommendations);
    };

    fetchRecommendations();
  }, [cartState.lineItems]);

  const fetchCart = async () => {
    try {
      const response = await fetch('/cart?view=jsondata');
      const data = await response.json();

      let lineItems = data.cart.items;

      // Add selling plan details to lineItems
      if (data.items_with_selling_plans?.length > 0) {
        lineItems = data.cart.items.map((item) => {
          const itemWithSellingPlan = data.items_with_selling_plans.find(
            (plan) => plan.key === item.key && plan.selling_plans?.length > 0,
          );

          if (itemWithSellingPlan) {
            return {
              ...item,
              selling_plans: itemWithSellingPlan.selling_plans,
            };
          }
          return item;
        });
      }
      setCartState({
        itemCount: data.cart.item_count,
        subtotal: data.cart.items_subtotal_price,
        subtotalVal: data.cart.items_subtotal_price / 100,
        lineItems: lineItems,
      });
      updateCartCount(data.cart.item_count);
    } catch (error) {
      console.error('Error fetching Shopify cart:', error);
    }
  };

  const show = (fetch = true) => {
    dialogRef.current?.showModal();
    document.body.classList.add('scroll-lock');
    disableBodyScroll();

    if (fetch) fetchCart();

    setTimeout(() => {
      setCartDrawerLoaded(true);
    }, 10);
  };

  const hide = () => {
    setCartDrawerLoaded(false);
    setTimeout(() => {
      dialogRef.current?.close();
    }, 300);
  };

  const runOnDialogClose = () => {
    document.body.classList.remove('scroll-lock');
    enableBodyScroll();
    setCartDrawerLoaded(false);
    setSubsTooltip({ visibleKey: null });
  };

  const dialogEscapeHandler = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      hide();
    }
  };

  const handleOuterClick = (e) => {
    if (e.target === e.currentTarget) {
      hide();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="cart-drawer-wrapper backdrop:hidden"
      aria-labelledby="cartDrawerTitle"
      onKeyDown={dialogEscapeHandler}
    >
      <div
        onMouseDown={handleOuterClick}
        className={`fixed inset-0 z-40 flex justify-end bg-black/40 transition-opacity duration-300 ${
          !cartDrawerLoaded ? 'opacity-0' : ''
        }`}
      >
        <div
          className={`cart-drawer-content relative flex h-full max-h-screen w-full max-w-screen-xxs flex-col bg-white transition-transform duration-300 lg:max-w-[480px] ${
            !cartDrawerLoaded ? 'translate-x-full' : ''
          }`}
        >
          <div className="cart-drawer-header border-p-quillGray flex items-center justify-between gap-5 border-b p-4 lg:px-6 lg:py-5">
            <h2
              id="cartDrawerTitle"
              className="flex items-center gap-1.5 text-xl font-semibold text-p-darkTeal lg:text-2xl"
            >
              {cartConfig.header_title}
              <span className="flex-shrink-0 text-sm font-normal">
                ({cartState.itemCount}{' '}
                {cartState.itemCount === 1 ? 'item' : 'items'})
              </span>
            </h2>
            <button onClick={hide} aria-label="Close Cart Drawer">
              {renderIcon('close')}
            </button>
          </div>
          <div className="cart-drawer-body relative z-[1] flex-grow overflow-y-auto px-4 lg:px-6">
            {/* <ProgressBar
              progressBarDetails={progressBarDetails}
              cartConfig={cartConfig}
            ></ProgressBar> */}
            {/* Line Items */}
            {cartState.lineItems.length > 0 ? (
              <div>
                {cartState.lineItems.map((item, index) => (
                  <LineItem
                    key={item.key}
                    item={item}
                    cartConfig={cartConfig}
                    loading={loading}
                    setLoading={setLoading}
                    subsTooltip={subsTooltip}
                    setSubsTooltip={setSubsTooltip}
                    isNotSoloLast={
                      cartState.lineItems.length > 1 &&
                      index === cartState.lineItems.length - 1
                    }
                  ></LineItem>
                ))}
              </div>
            ) : (
              /* Empty Cart content when there are no line items */
              <EmptyCart cartConfig={cartConfig}></EmptyCart>
            )}
            {/* Confetti spray animation */}
            {showconfettiSpray && (
              <Lottie
                animationData={confettiSpray}
                loop={false}
                className="absolute top-0"
                onComplete={() => setShowconfettiSpray(false)}
              />
            )}
            {/* Recommended Products */}
            {cartConfig.show_recommendations &&
              recommendedProducts?.length > 0 && (
                <Recommendations
                  recommendedProducts={recommendedProducts}
                  cartConfig={cartConfig}
                  loading={loading}
                  setLoading={setLoading}
                ></Recommendations>
              )}
          </div>
          {cartState.lineItems.length > 0 && (
            <div className="cart-drawer-footer border-p-quillGray flex flex-col gap-3 border-t p-4">
              <div className="flex items-center justify-between">
                <span className="text-p-darkTeal">
                  Subtotal ({cartState.itemCount}{' '}
                  {cartState.itemCount === 1 ? 'item' : 'items'})
                </span>
                <span className="font-semibold text-p-darkTeal">
                  {getDisplayPrice(cartState.subtotal)}
                </span>
              </div>
              <div>
                <a
                  className="btn flex w-full items-center  justify-center px-5 py-3 "
                  href="/checkout"
                >
                  {cartConfig.checkout_button_text}
                </a>
              </div>
              <div>
                <p className="text-center text-xs text-p-darkTeal">
                  {cartConfig.checkout_info_text}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
};

export default CartDrawer;
